<script setup>
import { formatNumber, formatPercent } from '../Utils/numberFormat.js'
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import { useHttpErrorsHandler } from '../Composables/httpErrorsHandler.js'

const props = defineProps({
    // 'network' : vue réseau (admins / back-office) ; 'center' : centres du manager + manqués à rappeler
    mode: { type: String, default: 'center' },
})

const { httpErrorsHandler } = useHttpErrorsHandler()

const CALLBACK_HELP = "Appels manqués (plusieurs tentatives d'un client non joint comptent pour une) ou messages laissés ce mois-ci, dont le client a été joint dans les 30 jours par n'importe quel centre : rappelé et décroché, ou il a rappelé lui-même et a été pris. Chiffre provisoire tant que les manqués du mois ont moins de 30 jours."
const PENDING_HELP = "Appels manqués et messages des 7 derniers jours dont le client n'a été joint par aucun centre depuis (un rappel sans réponse ne suffit pas). Liste à jour à chaque affichage."

const loading = ref(true)
const data = ref(null)

const formatDelay = (minutes) => {
    if (minutes === null || minutes === undefined) return '—'
    if (minutes < 60) return `${minutes} min`
    const hours = minutes / 60
    return hours < 48 ? `${Math.round(hours)} h` : `${Math.round(hours / 24)} j`
}
// Seuils de couleur [vert à partir de, orange à partir de], identiques sur tous les écrans d'appels
const ANSWER_RATE_TARGETS = [70, 50]
const REACHED_RATE_TARGETS = [90, 75]

const rateClass = (rate, good, average) => {
    if (rate === null || rate === undefined) return 'bg-light text-muted'
    if (rate >= good) return 'bg-success-subtle text-success'
    if (rate >= average) return 'bg-warning-subtle text-warning'
    return 'bg-danger-subtle text-danger'
}

const monthLabel = computed(() => {
    if (!data.value?.month) return ''
    const [year, month] = data.value.month.split('-').map(Number)
    const label = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(new Date(year, month - 1, 1))
    return label.charAt(0).toUpperCase() + label.slice(1)
})

// Fraîcheur des chiffres (mis en cache 5 min côté serveur)
const computedAtLabel = computed(() => {
    if (!data.value?.computed_at) return ''
    return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(new Date(data.value.computed_at)).replace(':', ' h ')
})

const totals = computed(() => data.value?.totals ?? {})
const callbacks = computed(() => data.value?.callbacks ?? {})

// Vue réseau : les centres dont les clients sont le moins joints après un manqué (au moins 5 manqués pour être comparables)
const centersToWatch = computed(() => (data.value?.centers ?? [])
    .filter((center) => (center.callbacks?.episodes ?? 0) >= 5)
    .sort((a, b) => (a.callbacks.reached_rate ?? 0) - (b.callbacks.reached_rate ?? 0))
    .slice(0, 5))

const kpis = computed(() => [
    {
        label: 'Appels entrants',
        value: formatNumber(totals.value.incoming),
        sub: `${formatNumber(totals.value.missed + totals.value.voicemail)} non décrochés`,
        icon: 'mdi-phone-incoming',
        color: 'success',
        help: `Pendant les horaires d'ouverture. En plus : ${formatNumber(totals.value.out_of_hours)} appels arrivés pendant la fermeture, comptés à part (hors taux de décroché).`,
    },
    { label: 'Taux de décroché', value: formatPercent(totals.value.answer_rate), sub: `${formatNumber(totals.value.answered)} décrochés`, icon: 'mdi-phone-check', color: 'secondary', badge: rateClass(totals.value.answer_rate, ...ANSWER_RATE_TARGETS) },
    { label: 'Clients joints', value: formatPercent(callbacks.value.reached_rate), sub: `dont ${formatPercent(callbacks.value.callback_rate)} rappelés`, icon: 'mdi-phone-return', color: 'warning', help: CALLBACK_HELP, badge: rateClass(callbacks.value.reached_rate, ...REACHED_RATE_TARGETS) },
    { label: 'Clients pas encore joints', value: formatNumber(callbacks.value.unreached), sub: `sur ${formatNumber(callbacks.value.episodes)} manqués ce mois-ci`, icon: 'mdi-phone-alert', color: 'danger', help: "Manqués dont le client n'a pas encore été joint. Chacun a 30 jours pour l'être : le chiffre baisse au fil des rappels." },
])

onMounted(() => {
    axios.get('/api/call-statistics/dashboard')
        .then((response) => {
            data.value = response.data.data
        })
        .catch((error) => httpErrorsHandler(error))
        .finally(() => {
            loading.value = false
        })
})
</script>

<template>
    <div class="card">
        <div class="card-header align-items-center d-flex">
            <h4 class="card-title mb-0 flex-grow-1">
                <i class="mdi mdi-phone me-2 align-middle fs-4"></i>
                Appels <span class="text-muted fw-normal" v-if="monthLabel">· {{ monthLabel }}</span>
            </h4>
            <div class="flex-shrink-0 d-flex align-items-center gap-3">
                <span class="text-muted fs-12 d-none d-md-inline" v-if="computedAtLabel" v-tooltip="'Chiffres actualisés toutes les 5 minutes'">
                    <i class="mdi mdi-refresh align-middle"></i> Mis à jour à {{ computedAtLabel }}
                </span>
                <router-link :to="{ name: 'statistics.index' }" class="btn btn-soft-dark btn-sm shadow-none">Statistiques</router-link>
            </div>
        </div>

        <div class="card-body d-flex align-items-center justify-content-center py-5" v-if="loading">
            <div class="spinner-border text-secondary" role="status"></div>
        </div>

        <template v-else-if="data">
            <!-- Indicateurs du mois : une bande, séparateurs entre les colonnes -->
            <div class="card-body p-0">
                <div class="row row-cols-xxl-4 row-cols-2 g-0 call-kpis">
                    <div class="col" v-for="kpi in kpis" :key="kpi.label">
                        <div class="py-3 px-3 py-sm-4 px-sm-4 d-flex align-items-center gap-3">
                            <!-- Téléphone : deux indicateurs par ligne, sans icône -->
                            <div class="avatar-sm flex-shrink-0 d-none d-sm-block">
                                <span class="avatar-title rounded-circle fs-3" :class="`bg-${kpi.color}-subtle text-${kpi.color}`"><i class="mdi" :class="kpi.icon"></i></span>
                            </div>
                            <div class="flex-grow-1 overflow-hidden">
                                <p class="text-uppercase fw-medium text-muted fs-12 mb-1">
                                    {{ kpi.label }}&nbsp;<i class="mdi mdi-information-outline" v-if="kpi.help" v-tooltip="kpi.help"></i>
                                </p>
                                <h4 class="mb-1 ff-secondary">
                                    <span v-if="kpi.badge" class="badge fs-15 px-2" :class="kpi.badge">{{ kpi.value }}</span>
                                    <span v-else>{{ kpi.value }}</span>
                                </h4>
                                <p class="text-muted fs-12 mb-0">{{ kpi.sub }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Vue réseau : centres à surveiller -->
            <template v-if="props.mode === 'network'">
                <div class="card-header border-top align-items-center d-flex">
                    <h6 class="card-title fs-14 mb-0 flex-grow-1">Centres qui joignent le moins leurs clients</h6>
                </div>
                <div class="card-body">
                    <div class="table-responsive table-card" v-if="centersToWatch.length">
                        <table class="table table-hover table-nowrap align-middle mb-0">
                            <thead class="table-light">
                                <tr class="text-muted">
                                    <th scope="col">Centre</th>
                                    <th scope="col" class="text-center">Taux de décroché</th>
                                    <th scope="col" class="text-center">Clients joints</th>
                                    <th scope="col" class="text-center">Clients non joints</th>
                                    <th scope="col" class="text-center">Délai moyen de rappel</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="center in centersToWatch" :key="center.center_id">
                                    <td class="fw-medium">{{ center.center_name }}</td>
                                    <td class="text-center"><span class="badge fs-11" :class="rateClass(center.answer_rate, ...ANSWER_RATE_TARGETS)">{{ formatPercent(center.answer_rate) }}</span></td>
                                    <td class="text-center"><span class="badge fs-11" :class="rateClass(center.callbacks.reached_rate, ...REACHED_RATE_TARGETS)">{{ formatPercent(center.callbacks.reached_rate) }}</span></td>
                                    <td class="text-center text-danger fw-semibold">{{ formatNumber(center.callbacks.unreached) }}</td>
                                    <td class="text-center">{{ formatDelay(center.callbacks.avg_delay_minutes) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="text-muted text-center mb-0" v-else>Pas encore assez d'appels manqués ce mois-ci pour comparer les centres.</p>
                </div>
            </template>

            <!-- Vue centre : manqués à rappeler -->
            <template v-else>
                <div class="card-header border-top align-items-center d-flex">
                    <h6 class="card-title fs-14 mb-0 flex-grow-1">
                        À rappeler&nbsp;<i class="mdi mdi-information-outline text-muted" v-tooltip="PENDING_HELP"></i>
                    </h6>
                    <span class="badge bg-danger-subtle text-danger" v-if="data.pending_total">{{ formatNumber(data.pending_total) }}</span>
                </div>
                <div class="card-body">
                    <div class="table-responsive table-card" v-if="data.pending.length">
                        <table class="table table-hover table-nowrap align-middle mb-0">
                            <thead class="table-light">
                                <tr class="text-muted">
                                    <th scope="col">Type</th>
                                    <th scope="col">Client</th>
                                    <th scope="col">Centre</th>
                                    <th scope="col">Dernier appel</th>
                                    <th scope="col" class="text-center">Rappeler</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="call in data.pending" :key="call.id">
                                    <td>
                                        <span class="badge fs-11" :class="call.state === 'voicemail' ? 'bg-warning-subtle text-warning' : 'bg-danger-subtle text-danger'">
                                            {{ call.state === 'voicemail' ? 'Messagerie' : 'Manqué' }}<span v-if="call.missed_call_count > 1"> ({{ call.missed_call_count }})</span>
                                        </span>
                                    </td>
                                    <td>
                                        <router-link :to="{ name: 'clients.show', params: { id: call.client_id } }" class="fw-medium text-secondary">{{ call.client_name || call.phone_number }}</router-link>
                                    </td>
                                    <td>{{ call.center_name }}</td>
                                    <td>{{ call.start_time_fr }}</td>
                                    <td class="text-center">
                                        <a :href="`tel:${call.phone_number}`" class="btn btn-sm btn-soft-success shadow-none" v-if="call.phone_number">
                                            <i class="mdi mdi-phone align-middle me-1"></i>{{ call.phone_number }}
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="text-muted text-center mb-0" v-if="!data.pending.length">
                        <i class="mdi mdi-check-circle-outline text-success align-middle me-1"></i>
                        Aucun appel manqué en attente de rappel
                    </p>
                </div>
                <div class="card-footer d-flex align-items-center justify-content-center py-2 fs-12" v-if="data.pending_total > data.pending.length">
                    <router-link :to="{ name: 'calls.list', query: { type: 'to_call_back' } }" class="link-secondary">
                        + {{ formatNumber(data.pending_total - data.pending.length) }} autres appels à rappeler <i class="ri-arrow-right-line align-middle"></i>
                    </router-link>
                </div>
            </template>
        </template>
    </div>
</template>

<style scoped>
/* Séparateurs entre les indicateurs : grille 2 × 2, puis une seule ligne sur grand écran */
.call-kpis > .col:nth-child(even) {
    border-left: 1px solid var(--vz-border-color);
}

.call-kpis > .col:nth-child(n + 3) {
    border-top: 1px solid var(--vz-border-color);
}

@media (min-width: 1400px) {
    .call-kpis > .col:nth-child(n + 3) {
        border-top: 0;
    }

    .call-kpis > .col + .col {
        border-left: 1px solid var(--vz-border-color);
    }
}
</style>
